import { useDialogs } from '../components/ui'
import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { useAuth } from '../contexts/AuthContext'
import { authApiService } from '../services/authApiService'
import { webhooksApiService } from '../services/webhooksApiService'
import type { Webhook, WebhookCreated } from '../services/webhooksApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, componentHeight, fontFamily, fontSize, radius, shadow, spacing, transition } from '../styles/theme'

type Tab = 'profile' | 'mfa' | 'webhooks'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: fontSize.base,
  color: colors.text,
  boxSizing: 'border-box',
  outline: 'none',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: fontSize.sm,
  fontWeight: 500,
  color: colors.text,
  marginBottom: spacing.xs,
}

function ProfileTab() {
  const { user, refresh } = useAuth()
  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileMsg, setProfileMsg] = useState('')

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [savingPw, setSavingPw] = useState(false)
  const [pwMsg, setPwMsg] = useState('')
  const [pwErr, setPwErr] = useState('')

  const [newEmail, setNewEmail] = useState('')
  const [emailPw, setEmailPw] = useState('')
  const [emailStage, setEmailStage] = useState<'idle' | 'code'>('idle')
  const [emailCode, setEmailCode] = useState('')
  const [emailBusy, setEmailBusy] = useState(false)
  const [emailMsg, setEmailMsg] = useState('')
  const [emailErr, setEmailErr] = useState('')

  const cardStyle: React.CSSProperties = {
    background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
    padding: spacing.xl, marginBottom: spacing.lg,
  }

  async function saveProfile() {
    setSavingProfile(true); setProfileMsg('')
    try {
      await authApiService.updateProfile(firstName.trim(), lastName.trim() || null)
      await refresh?.()
      setProfileMsg('Saved.')
    } catch (e) {
      setProfileMsg(e instanceof ApiError ? e.message : 'Could not save profile')
    } finally { setSavingProfile(false) }
  }

  async function requestEmailChange() {
    setEmailErr(''); setEmailMsg(''); setEmailBusy(true)
    try {
      await authApiService.changeEmail(newEmail.trim(), emailPw)
      setEmailStage('code'); setEmailMsg('We sent a code to the new address. Enter it below to confirm.')
    } catch (e) {
      setEmailErr(e instanceof ApiError ? e.message : 'Could not start email change')
    } finally { setEmailBusy(false) }
  }

  async function confirmEmailChange() {
    setEmailErr(''); setEmailMsg(''); setEmailBusy(true)
    try {
      await authApiService.verifyEmailChange(emailCode.trim())
      await refresh?.()
      setEmailStage('idle'); setNewEmail(''); setEmailPw(''); setEmailCode('')
      setEmailMsg('Email updated.')
    } catch (e) {
      setEmailErr(e instanceof ApiError ? e.message : 'Could not confirm email change')
    } finally { setEmailBusy(false) }
  }

  async function signOutEverywhere() {
    try { await authApiService.signOutEverywhere(); window.location.href = '/login' }
    catch { /* best-effort */ }
  }

  async function changePassword() {
    setPwErr(''); setPwMsg('')
    if (next.length < 8) { setPwErr('New password must be at least 8 characters.'); return }
    if (next !== confirmPw) { setPwErr('New passwords do not match.'); return }
    setSavingPw(true)
    try {
      await authApiService.changePassword(current, next)
      setPwMsg('Password changed. You may need to sign in again on other devices.')
      setCurrent(''); setNext(''); setConfirmPw('')
    } catch (e) {
      setPwErr(e instanceof ApiError ? e.message : 'Could not change password')
    } finally { setSavingPw(false) }
  }

  return (
    <div style={{ maxWidth: 480 }}>
      <div style={cardStyle}>
        <div style={{ marginBottom: spacing.md }}>
          <label style={labelStyle}>First name</label>
          <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: spacing.md }}>
          <label style={labelStyle}>Last name</label>
          <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: spacing.md }}>
          <label style={labelStyle}>Email</label>
          <input type="text" value={user?.email ?? ''} style={inputStyle} readOnly />
        </div>
        <button onClick={saveProfile} disabled={savingProfile || !firstName.trim()}
          style={{ padding: '8px 16px', border: 'none', borderRadius: radius.sm, background: colors.primary, color: colors.onPrimary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>
          {savingProfile ? 'Saving…' : 'Save profile'}
        </button>
        {profileMsg && <span style={{ marginLeft: spacing.md, fontSize: fontSize.sm, color: colors.textSecondary }}>{profileMsg}</span>}
      </div>

      <div style={cardStyle}>
        <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.md }}>Change password</div>
        <div style={{ marginBottom: spacing.md }}>
          <label style={labelStyle}>Current password</label>
          <input type="password" value={current} onChange={e => setCurrent(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: spacing.md }}>
          <label style={labelStyle}>New password</label>
          <input type="password" value={next} onChange={e => setNext(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: spacing.md }}>
          <label style={labelStyle}>Confirm new password</label>
          <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={inputStyle} />
        </div>
        {pwErr && <div style={{ color: colors.error, fontSize: fontSize.sm, marginBottom: spacing.sm }}>{pwErr}</div>}
        {pwMsg && <div style={{ color: colors.success, fontSize: fontSize.sm, marginBottom: spacing.sm }}>{pwMsg}</div>}
        <button onClick={changePassword} disabled={savingPw || !current || !next}
          style={{ padding: '8px 16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.text, fontSize: fontSize.sm, cursor: 'pointer' }}>
          {savingPw ? 'Changing…' : 'Change password'}
        </button>
      </div>

      <div style={cardStyle}>
        <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.md }}>Change email</div>
        {emailStage === 'idle' ? (
          <>
            <div style={{ marginBottom: spacing.md }}>
              <label style={labelStyle}>New email</label>
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: spacing.md }}>
              <label style={labelStyle}>Current password</label>
              <input type="password" value={emailPw} onChange={e => setEmailPw(e.target.value)} style={inputStyle} />
            </div>
            <button onClick={requestEmailChange} disabled={emailBusy || !newEmail.trim() || !emailPw}
              style={{ padding: '8px 16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.text, fontSize: fontSize.sm, cursor: 'pointer' }}>
              {emailBusy ? 'Sending…' : 'Send confirmation code'}
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: spacing.md }}>
              <label style={labelStyle}>Confirmation code (sent to {newEmail})</label>
              <input type="text" inputMode="numeric" value={emailCode} onChange={e => setEmailCode(e.target.value)} style={inputStyle} />
            </div>
            <button onClick={confirmEmailChange} disabled={emailBusy || !emailCode.trim()}
              style={{ padding: '8px 16px', border: 'none', borderRadius: radius.sm, background: colors.primary, color: colors.onPrimary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>
              {emailBusy ? 'Confirming…' : 'Confirm new email'}
            </button>
          </>
        )}
        {emailErr && <div style={{ color: colors.error, fontSize: fontSize.sm, marginTop: spacing.sm }}>{emailErr}</div>}
        {emailMsg && <div style={{ color: colors.success, fontSize: fontSize.sm, marginTop: spacing.sm }}>{emailMsg}</div>}
      </div>

      <div style={cardStyle}>
        <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>Sessions</div>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
          Sign out of every device, including this one. You will need to sign in again.
        </div>
        <button onClick={signOutEverywhere}
          style={{ padding: '8px 16px', border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, background: 'none', color: colors.error, fontSize: fontSize.sm, cursor: 'pointer' }}>
          Sign out everywhere
        </button>
      </div>
    </div>
  )
}

export function MfaTab() {
  const [isEnabled, setIsEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [code, setCode] = useState('')
  const [enrollData, setEnrollData] = useState<{ method: string } | null>(null)
  const [action, setAction] = useState<'idle' | 'enrolling' | 'totp' | 'disabling'>('idle')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [recoveryCount, setRecoveryCount] = useState<number | null>(null)
  const [isTotp, setIsTotp] = useState(false)
  const [totp, setTotp] = useState<{ secret: string; otpauthUri: string; qr: string } | null>(null)

  useEffect(() => {
    authApiService.mfaStatus()
      .then(async s => {
        setIsEnabled(s.isEnabled)
        setIsTotp(s.methodId === 20)
        if (s.isEnabled) {
          const { count } = await authApiService.mfaRecoveryCodesCount()
          setRecoveryCount(count)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  async function startEnroll() {
    setError('')
    setSuccess('')
    try {
      const data = await authApiService.mfaEnroll()
      setEnrollData(data)
      setAction('enrolling')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to start enrollment')
    }
  }

  async function startTotp() {
    setError('')
    setSuccess('')
    try {
      const data = await authApiService.mfaTotpEnroll()
      const qr = await QRCode.toDataURL(data.otpauthUri, { margin: 1, width: 200 })
      setTotp({ ...data, qr })
      setCode('')
      setAction('totp')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to start authenticator setup')
    }
  }

  async function confirmEnroll() {
    setError('')
    try {
      const totpFlow = action === 'totp'
      const res = totpFlow ? await authApiService.mfaTotpConfirm(code) : await authApiService.mfaEnrollConfirm(code)
      setIsEnabled(true)
      setIsTotp(totpFlow)
      setTotp(null)
      setAction('idle')
      setCode('')
      setEnrollData(null)
      if (res?.recoveryCodes?.length) {
        setRecoveryCodes(res.recoveryCodes)
        setRecoveryCount(res.recoveryCodes.length)
      }
      setSuccess('MFA enabled successfully.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verification failed')
    }
  }

  async function disable() {
    setError('')
    try {
      await authApiService.mfaDisable(code)
      setIsEnabled(false)
      setAction('idle')
      setCode('')
      setSuccess('MFA disabled.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to disable MFA')
    }
  }

  if (loading) return <div style={{ color: colors.textSecondary }}>Loading...</div>

  return (
    <div style={{ maxWidth: 480 }}>
      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}
      {success && (
        <div style={{
          background: colors.successLight, color: colors.success,
          border: `1px solid ${colors.successBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{success}</div>
      )}

      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        padding: spacing.lg,
        marginBottom: spacing.lg,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: fontSize.md, fontWeight: 600, color: colors.text }}>
              Two-factor authentication
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
              {isEnabled ? `MFA is currently enabled via ${isTotp ? 'an authenticator app' : 'email'}.` : 'Add an extra layer of security to your account.'}
            </div>
          </div>
          <div style={{
            padding: '3px 10px',
            borderRadius: 999,
            background: isEnabled ? colors.successLight : colors.background,
            color: isEnabled ? colors.success : colors.textSecondary,
            fontSize: fontSize.sm,
            fontWeight: 500,
            border: `1px solid ${isEnabled ? colors.successBorder : colors.border}`,
          }}>
            {isEnabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>
      </div>

      {action === 'idle' && (
        isEnabled ? (
          <button
            onClick={async () => {
              setError('')
              setSuccess('')
              try {
                // Authenticator users confirm with an app code; only email users need one sent.
                if (!isTotp) await authApiService.mfaEnroll()
                setAction('disabling')
                setCode('')
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Failed to send verification code')
              }
            }}
            style={{
              padding: '8px 16px',
              background: 'none',
              border: `1px solid ${colors.error}`,
              borderRadius: radius.sm,
              fontSize: fontSize.base,
              color: colors.error,
              cursor: 'pointer',
            }}
          >
            Disable MFA
          </button>
        ) : (
          <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
            <button
              onClick={startTotp}
              style={{
                padding: '8px 16px',
                background: colors.primary,
                color: colors.onPrimary,
                border: 'none',
                borderRadius: radius.sm,
                fontSize: fontSize.base,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Use an authenticator app (recommended)
            </button>
            <button
              onClick={startEnroll}
              style={{
                padding: '8px 16px',
                background: 'none',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: fontSize.base,
                color: colors.text,
                cursor: 'pointer',
              }}
            >
              Email me a code instead
            </button>
          </div>
        )
      )}

      {action === 'totp' && totp && (
        <div style={{ marginBottom: spacing.md }}>
          <p style={{ fontSize: fontSize.base, color: colors.text, marginBottom: spacing.sm }}>
            Add DocMorphia to any free authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, Apple
            Passwords…), then enter the 6-digit code it shows.
          </p>
          <ul style={{ fontSize: fontSize.sm, color: colors.textSecondary, paddingLeft: spacing.lg, margin: `0 0 ${spacing.sm}` }}>
            <li><strong>On a computer:</strong> scan this QR code with the app on your phone.</li>
            <li><strong>On this phone, any app:</strong> copy the setup key, then in the app choose “Enter a setup key” (Google) or
              “Add account → Other” (Microsoft) and paste it.</li>
            <li><strong>On this phone, default app:</strong> the link opens your phone’s default code app (Apple Passwords on iPhone);
              the phone decides which app, so use the key to pick a different one.</li>
          </ul>
          <img src={totp.qr} alt="Authenticator setup QR code" width={200} height={200} style={{ display: 'block', marginBottom: spacing.sm }} />
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap', marginBottom: spacing.sm }}>
            <code style={{ wordBreak: 'break-all', fontSize: fontSize.sm }}>{totp.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
            <button type="button" onClick={async () => {
              try { await navigator.clipboard.writeText(totp.secret); setSuccess('Setup key copied.') }
              catch { setError('Could not copy — select the key and copy it manually.') }
            }} style={{ padding: '4px 10px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: fontSize.sm }}>
              Copy setup key
            </button>
          </div>
          <a href={totp.otpauthUri} style={{ display: 'inline-block', color: colors.primary, fontWeight: 600, fontSize: fontSize.sm }}>
            Add to this phone’s default code app
          </a>
        </div>
      )}

      {(action === 'enrolling' || action === 'totp') && (
        <div>
          {action === 'enrolling' && (
            <p style={{ fontSize: fontSize.base, color: colors.text, marginBottom: spacing.md }}>
              A verification code has been sent to your email. Enter it below to complete setup.
            </p>
          )}
          <div style={{ marginBottom: spacing.md }}>
            <label style={labelStyle}>Verification code</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              style={{ ...inputStyle, letterSpacing: '0.2em', textAlign: 'center' }}
              maxLength={6}
              placeholder="000000"
            />
          </div>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <button
              onClick={() => { setAction('idle'); setCode(''); setEnrollData(null); setTotp(null) }}
              style={{
                padding: '8px 16px', background: 'none',
                border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                fontSize: fontSize.base, cursor: 'pointer', color: colors.text,
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmEnroll}
              style={{
                padding: '8px 16px', background: colors.primary,
                color: colors.onPrimary, border: 'none', borderRadius: radius.sm,
                fontSize: fontSize.base, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {recoveryCodes && (
        <div style={{
          marginTop: spacing.lg,
          background: colors.warningLight,
          border: `1px solid ${colors.warningBorder}`,
          borderRadius: radius.md,
          padding: spacing.md,
        }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.warning, marginBottom: spacing.sm }}>
            Save these recovery codes — they won't be shown again.
          </div>
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            padding: spacing.sm,
            fontFamily: 'monospace',
            fontSize: fontSize.sm,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '2px 16px',
          }}>
            {recoveryCodes.map(c => <span key={c}>{c}</span>)}
          </div>
          <button
            onClick={() => setRecoveryCodes(null)}
            style={{
              marginTop: spacing.sm,
              padding: '4px 10px',
              background: 'none',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              cursor: 'pointer',
            }}
          >
            I've saved these
          </button>
        </div>
      )}

      {!recoveryCodes && isEnabled && action === 'idle' && (
        <div style={{ marginTop: spacing.lg }}>
          <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>
            {recoveryCount !== null ? `${recoveryCount} recovery code${recoveryCount === 1 ? '' : 's'} remaining` : ''}
          </div>
          <button
            onClick={async () => {
              setError('')
              try {
                const res = await authApiService.mfaGenerateRecoveryCodes()
                setRecoveryCodes(res.recoveryCodes)
                setRecoveryCount(res.recoveryCodes.length)
                setSuccess('')
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'Failed to generate recovery codes')
              }
            }}
            style={{
              padding: '6px 14px',
              background: 'none',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              cursor: 'pointer',
              color: colors.text,
            }}
          >
            Regenerate recovery codes
          </button>
        </div>
      )}

      {action === 'disabling' && (
        <div>
          <p style={{ fontSize: fontSize.base, color: colors.text, marginBottom: spacing.md }}>
            {isTotp ? 'Enter the current code from your authenticator app to disable MFA.' : 'Enter the verification code sent to your email to disable MFA.'}
          </p>
          <div style={{ marginBottom: spacing.md }}>
            <label style={labelStyle}>Verification code</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              style={{ ...inputStyle, letterSpacing: '0.2em', textAlign: 'center' }}
              maxLength={6}
              placeholder="000000"
            />
          </div>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <button
              onClick={() => { setAction('idle'); setCode('') }}
              style={{
                padding: '8px 16px', background: 'none',
                border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                fontSize: fontSize.base, cursor: 'pointer', color: colors.text,
              }}
            >
              Cancel
            </button>
            <button
              onClick={disable}
              style={{
                padding: '8px 16px', background: colors.error,
                color: colors.onPrimary, border: 'none', borderRadius: radius.sm,
                fontSize: fontSize.base, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Disable MFA
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function WebhooksTab() {
  const dialogs = useDialogs()
  const [hooks, setHooks] = useState<Webhook[]>([])
  const [loading, setLoading] = useState(true)
  const [url, setUrl] = useState('')
  const [desc, setDesc] = useState('')
  const [creating, setCreating] = useState(false)
  const [newSecret, setNewSecret] = useState<{ id: string; secret: string } | null>(null)
  const [err, setErr] = useState('')
  const [listErr, setListErr] = useState('')

  useEffect(() => {
    webhooksApiService.list()
      .then(setHooks)
      .catch(e => setListErr(e instanceof ApiError ? e.message : 'Failed to load webhooks'))
      .finally(() => setLoading(false))
  }, [])

  async function handleCreate() {
    if (!url.trim()) return
    setCreating(true); setErr('')
    try {
      const created: WebhookCreated = await webhooksApiService.create(url.trim(), desc.trim() || undefined)
      setHooks(prev => [created, ...prev])
      setNewSecret({ id: created.webhookId, secret: created.secret })
      setUrl(''); setDesc('')
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to create webhook')
    } finally { setCreating(false) }
  }

  async function handleDelete(id: string) {
    if (!(await dialogs.confirm({ title: 'Delete this webhook?', message: 'Events stop being sent to it.', confirmLabel: 'Delete', danger: true }))) return
    setListErr('')
    try {
      await webhooksApiService.delete(id)
    } catch (e) {
      setListErr(e instanceof ApiError ? e.message : 'Failed to delete webhook')
      return
    }
    setHooks(prev => prev.filter(h => h.webhookId !== id))
    if (newSecret?.id === id) setNewSecret(null)
  }

  const rowStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.sm,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  }
  const thStyle: React.CSSProperties = {
    ...rowStyle,
    fontWeight: 600,
    color: colors.textSecondary,
    background: colors.background,
  }

  return (
    <>
    <div style={{ maxWidth: 700 }}>
      <p style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 0, marginBottom: spacing.lg }}>
        Register HTTPS endpoints to receive a signed <code>render.complete</code> event after every render.
        Payloads are signed with HMAC-SHA256 in the <code>X-DocMorphia-Signature</code> header.
      </p>

      {/* Add form */}
      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        padding: spacing.lg,
        boxShadow: shadow.sm,
        marginBottom: spacing.lg,
      }}>
        <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.md, fontSize: fontSize.base }}>
          Add Endpoint
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
          <input
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://your-server.com/webhook"
            style={inputStyle}
          />
          <input
            type="text"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder="Description (optional)"
            style={inputStyle}
          />
          {err && <div style={{ fontSize: fontSize.sm, color: colors.error }}>{err}</div>}
          <button
            onClick={handleCreate}
            disabled={creating || !url.trim()}
            style={{
              alignSelf: 'flex-start',
              padding: '7px 18px',
              background: url.trim() && !creating ? colors.primary : colors.textMuted,
              color: colors.onPrimary,
              border: 'none',
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              fontWeight: 600,
              cursor: url.trim() && !creating ? 'pointer' : 'default',
            }}
          >
            {creating ? 'Adding…' : 'Add Webhook'}
          </button>
        </div>
      </div>

      {/* Secret reveal */}
      {newSecret && (
        <div style={{
          background: colors.successLight,
          border: `1px solid ${colors.successBorder}`,
          borderRadius: radius.sm,
          padding: spacing.md,
          marginBottom: spacing.lg,
          fontSize: fontSize.sm,
        }}>
          <strong>Signing secret (shown once):</strong>{' '}
          <code style={{ fontFamily: 'monospace', userSelect: 'all' }}>{newSecret.secret}</code>
          <div style={{ color: colors.textSecondary, marginTop: 4 }}>
            Save this now — it will not be shown again.
          </div>
        </div>
      )}

      {/* Table */}
      {listErr && (
        <div role="alert" style={{ fontSize: fontSize.sm, color: colors.error, marginBottom: spacing.sm }}>{listErr}</div>
      )}
      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading…</div>
      ) : hooks.length === 0 ? (
        listErr ? null :
        <div style={{ color: colors.textMuted, fontSize: fontSize.sm }}>No webhooks configured.</div>
      ) : (
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
          boxShadow: shadow.sm,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>URL</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Last triggered</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {hooks.map(h => (
                <tr key={h.webhookId}>
                  <td style={{ ...rowStyle, fontFamily: 'monospace', fontSize: '11px', maxWidth: 260, wordBreak: 'break-all' }}>
                    {h.url}
                  </td>
                  <td style={{ ...rowStyle, color: colors.textSecondary }}>{h.description ?? '—'}</td>
                  <td style={{ ...rowStyle, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                    {h.lastTriggeredAt ? new Date(h.lastTriggeredAt).toLocaleString() : 'Never'}
                  </td>
                  <td style={rowStyle}>
                    <button
                      onClick={() => handleDelete(h.webhookId)}
                      style={{
                        padding: '3px 8px',
                        background: 'none',
                        border: `1px solid ${colors.errorBorder}`,
                        borderRadius: radius.sm,
                        fontSize: '11px',
                        color: colors.error,
                        cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
      {dialogs.element}
    </>
  )
}

export default function SettingsPage() {
  // ?tab=mfa deep-links to Security (the admin-MFA banner links here).
  const [tab, setTab] = useState<Tab>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'mfa' ? 'mfa' : 'profile')

  const tabs: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'mfa', label: 'Security' },
    { id: 'webhooks', label: 'Webhooks' },
  ]

  return (
    <div>
      <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.xl }}>
        Settings
      </h1>

      <div style={{
        display: 'flex',
        marginBottom: spacing.xl,
        gap: spacing.xs,
        padding: spacing.xs,
        background: colors.background,
        borderRadius: radius.full,
        alignSelf: 'flex-start',
      }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              background: tab === t.id ? colors.primaryLight : 'transparent',
              border: 'none',
              borderRadius: radius.full,
              fontSize: fontSize.base,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? colors.primary : colors.textSecondary,
              cursor: 'pointer',
              transition: transition.fast,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'mfa' && <MfaTab />}
      {tab === 'webhooks' && <WebhooksTab />}
    </div>
  )
}
